import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, concatMap, defaultIfEmpty, from, Subject, switchMap, take, tap } from 'rxjs';

import { TOURS } from '@app/entities/constants/tour.const';
import { TourKeys, TourType } from '@app/entities/enums/tour.enum';
import { NewsItem } from '@app/entities/interfaces/news/news.interface';
import { Tour } from '@app/entities/interfaces/tour.interface';
import { IStepOption, TourService } from 'ngx-ui-tour-md-menu';

import { TourUseCases } from './tour.use-cases.contract';
import { USER_USE_CASES, UserUseCases } from '../user/user.use-cases.contract';

import { PAGES } from '@/enums/pages.enum';
@Injectable()
export class TourUseCasesImpl implements TourUseCases {
  readonly appWalkthroughs: Tour[] = TOURS;
  #tourService: TourService = inject(TourService);
  #userUseCases: UserUseCases = inject(USER_USE_CASES);
  currentWalkthroughSteps$: Subject<IStepOption[]> = new Subject();
  stepIndex$: BehaviorSubject<number> = new BehaviorSubject(1);
  closeOutsideDisabled$: Subject<boolean> = new Subject();
  initTour$: Subject<PAGES> = new Subject();

  initService(walkthroughKeys: string[], news: NewsItem[]) {
    this.initTour$
      .pipe(
        switchMap((page) => {
          // Process walkthroughs first
          const walkthroughs$ = from(walkthroughKeys || []).pipe(
            concatMap((wk) => this.#processWalkthrough(page, wk as TourKeys, walkthroughKeys as TourKeys[])),
            defaultIfEmpty(null) // execute news even if there is not any walkthroughs
          );

          // Process news after walkthroughs
          const news$ = from(news || []).pipe(
            concatMap((item) => this.#processNews(page, item)),
            defaultIfEmpty(null)
          );

          // Execute first walkthroughs$, then news$
          return walkthroughs$.pipe(concatMap(() => news$));
        })
      )
      .subscribe();
  }

  getWalkthroughByKey(key: string): Tour | undefined {
    return this.appWalkthroughs.find((walkthrough) => walkthrough.keyName === key);
  }

  initTour(tour: Tour) {
    this.#tourService.initialize(tour.steps, {
      enableBackdrop: true,
      closeOnOutsideClick: false,
      backdropConfig: { backgroundColor: 'var(--transparent400)' },
      scrollContainer: 'body'
    });

    this.currentWalkthroughSteps$.next(tour.steps);
    this.startTour(tour.type === TourType.WALKTHROUGH);
  }

  startTour(isTourType: boolean) {
    this.#tourService.start();
    if (!isTourType) {
      return;
    }

    this.closeOutsideDisabled$.next(true);
  }

  hasPrev(step: IStepOption): boolean {
    return this.#tourService.hasPrev(step);
  }

  onPrevClick() {
    this.stepIndex$.next(this.stepIndex$.value - 1);
    this.#tourService.prev();
  }

  onNextClick(step: IStepOption) {
    if (this.#tourService.hasNext(step)) {
      this.stepIndex$.next(this.stepIndex$.value + 1);
      this.#tourService.next();
    } else {
      this.#tourService.end();
      this.closeOutsideDisabled$.next(false);
    }
  }

  #markNewsAsRead(n: NewsItem) {
    return this.#userUseCases.setAcceptNews(n);
  }

  #markWalkthroughAsSeen(walkthroughKey: TourKeys) {
    return this.#userUseCases.setWalkthroughSeen(walkthroughKey);
  }

  #processWalkthrough(page: string, wk: TourKeys, allWalkthroughs: TourKeys[]) {
    // Immediately initialize the walkthrough if appropriate
    this.#shouldInitWalkthrough(page, wk);

    // Wait for the tour to finish, mark as seen first, then update state
    return this.#tourService.end$.pipe(
      take(1),
      switchMap(() => this.#markWalkthroughAsSeen(wk)), // mark as seen first
      tap(() => {
        // Only after marking as seen, update state
        const index = allWalkthroughs.indexOf(wk);
        if (index > -1) {
          allWalkthroughs.splice(index, 1);
        }
        this.stepIndex$.next(1);
      })
    );
  }

  #processNews(page: string, item: NewsItem) {
    // Immediately initialize the news item if appropriate
    this.#shouldInitNews(page, item);

    // Wait for the tour to finish before marking as read and updating state
    return this.#tourService.end$.pipe(
      take(1),
      switchMap(() => this.#markNewsAsRead(item)),
      tap(() => (item.accepted = true))
    );
  }

  #shouldInitWalkthrough(page: string, key: TourKeys) {
    const stored = this.getWalkthroughByKey(key);
    if (page === stored?.section) {
      this.initTour(stored);
    }
  }

  #shouldInitNews(page: string, item: NewsItem) {
    const stored = this.getWalkthroughByKey(item.type);
    if (!item.accepted && page === stored?.section) {
      this.initTour(stored);
    }
  }
}
